import { useState, useEffect } from 'react';
import { Session, SessionStatus } from '@/types/session';
import { sessionStorage } from '@services/storage/session-storage';
import { logger } from '@utils/logger';

export function useSession(sessionId?: string) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
  }, [sessionId]);

  const loadSession = async (id: string) => {
    try {
      setLoading(true);
      const sessionData = await sessionStorage.getById(id);
      setSession(sessionData || null);
      setError(null);
    } catch (err) {
      logger.error('Failed to load session:', err);
      setError('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const updateSessionStatus = async (
    id: string,
    status: SessionStatus
  ): Promise<void> => {
    try {
      await sessionStorage.updateStatus(id, status);
      if (session) {
        setSession({ ...session, status });
      }
    } catch (err) {
      logger.error('Failed to update session status:', err);
      throw err;
    }
  };

  const endSession = async (id: string, notes?: string): Promise<void> => {
    try {
      await sessionStorage.endSession(id, notes);
      if (session) {
        setSession({
          ...session,
          status: 'completed',
          endTime: new Date(),
          notes,
        });
      }
    } catch (err) {
      logger.error('Failed to end session:', err);
      throw err;
    }
  };

  return {
    session,
    loading,
    error,
    updateSessionStatus,
    endSession,
    reload: sessionId ? () => loadSession(sessionId) : undefined,
  };
}
